import React, { useEffect, useRef, useState } from 'react';
import {
    createStaffSaveCardSession,
    staffSavePaymentMethod,
} from '../services/paymentsService.js';

/**
 * StaffAddCardForm
 * ================
 *
 * Staff counterpart to ``components/publicInvoice/AddPaymentMethod.js``.
 * Mounts the Rainforest Payment Component in *save-only* mode bound to
 * a freshly-minted ``payment_method_config_id``, listens for the
 * ``approved`` event, then POSTs the resulting payment_method_id back
 * to the staff save-card endpoint to land it in the vault.
 *
 * Used inside the "Charge Card on File" modal (and anywhere else staff
 * needs to manually key a card on a patient's behalf — phone payment,
 * in-office without terminal, etc.). The same metadata routing the
 * webhook handler relies on means the resulting card behaves
 * identically to one saved via public link or terminal — surcharge
 * preview, charge-on-file, auto-charge, all work.
 *
 * Props
 *   practiceId, patientId — vault scope.
 *   hasExistingCards      — when false, the "make default" checkbox is
 *                           force-checked and disabled. Vault upsert
 *                           already promotes the only ACTIVE card to
 *                           default automatically, so leaving it
 *                           unchecked would be misleading.
 *   onSaved(savedRow)     — fires after vault_upsert succeeds. Parent
 *                           should refresh its card list and (typically)
 *                           auto-select the new card.
 *   onCancel()            — fires when staff dismisses the form.
 */
export default function StaffAddCardForm({
    practiceId,
    patientId,
    hasExistingCards = true,
    onSaved,
    onCancel,
}) {
    const [sessionKey, setSessionKey] = useState(null);
    const [pmcId, setPmcId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    // First card on a patient is *always* the default — there's
    // nothing else to fall back to. Pre-check + disable when the
    // patient has no cards on file so staff can't toggle a misleading
    // "no" that the vault would silently override anyway.
    const [makeDefault, setMakeDefault] = useState(!hasExistingCards);
    const [error, setError] = useState(null);
    const componentRef = useRef(null);

    // Keep ``makeDefault`` honest if ``hasExistingCards`` flips while
    // the form is open (e.g. another tab vaulted a card behind the
    // staff's back). The ``!hasExistingCards`` branch hard-checks; the
    // other branch leaves the staff's existing toggle alone.
    useEffect(() => {
        if (!hasExistingCards) setMakeDefault(true);
    }, [hasExistingCards]);

    // Mint the save-only session as soon as we mount. Patient and
    // practice are stable for the lifetime of the form so a single
    // session-mint suffices; if staff hits Cancel and re-opens, the
    // parent should remount this component (don't try to reuse).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            const { ok, data } = await createStaffSaveCardSession(practiceId, patientId);
            if (cancelled) return;
            if (!ok || !data?.success) {
                // Server returns structured {success:false, error, details}
                // bodies on Rainforest/merchant-config failures — surface
                // the real message rather than a generic "API error: 400".
                setError(data?.error || 'Could not load the card form.');
                setLoading(false);
                return;
            }
            setSessionKey(data.session_key);
            setPmcId(data.payment_method_config_id);
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [practiceId, patientId]);

    // Wire the Rainforest event handlers once the element is mounted.
    // Mirror the public AddPaymentMethod listener set so we don't miss
    // the success signal across SDK versions.
    useEffect(() => {
        if (!sessionKey || !componentRef.current) return undefined;
        const el = componentRef.current;

        const handleApproved = async (event) => {
            const rawDetail = event?.detail;
            // Rainforest wraps event.detail in a one-element array
            // around the API-style envelope; older SDKs send the
            // envelope directly. Normalise both.
            const envelope = Array.isArray(rawDetail) ? rawDetail[0] : rawDetail;
            const data = (envelope && envelope.data) || envelope || {};
            const paymentMethodId =
                data.payment_method_id ||
                data.paymentMethodId ||
                data.payment_method?.id ||
                data.id ||
                envelope?.payment_method_id ||
                envelope?.id;
            if (!paymentMethodId) {
                setError('The card form did not return a payment method id.');
                return;
            }
            setSubmitting(true);
            setError(null);
            const { ok, data: resp } = await staffSavePaymentMethod(practiceId, patientId, {
                paymentMethodId,
                makeDefault,
            });
            setSubmitting(false);
            if (!ok || !resp?.success) {
                setError(resp?.error || 'Could not save this card.');
                return;
            }
            onSaved?.(resp.paymentMethod);
        };
        const handleDeclined = () => {
            setError('The card was declined. Please try a different card.');
        };
        const handleError = () => {
            setError('There was a problem with the card form. Please try again.');
        };

        el.addEventListener('approved', handleApproved);
        el.addEventListener('declined', handleDeclined);
        el.addEventListener('error', handleError);
        // Older SDKs use these event names for save-only flows; wire
        // them all so we don't quietly miss the success signal.
        el.addEventListener('payment-method', handleApproved);
        el.addEventListener('payment_method', handleApproved);
        el.addEventListener('tokenized', handleApproved);

        return () => {
            el.removeEventListener('approved', handleApproved);
            el.removeEventListener('declined', handleDeclined);
            el.removeEventListener('error', handleError);
            el.removeEventListener('payment-method', handleApproved);
            el.removeEventListener('payment_method', handleApproved);
            el.removeEventListener('tokenized', handleApproved);
        };
    }, [sessionKey, practiceId, patientId, makeDefault, onSaved]);

    return (
        <div style={{
            backgroundColor: '#fff',
            border: '1px solid #e3e7e4',
            borderRadius: 8,
            padding: 16,
            marginBottom: 12,
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 12,
            }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>Add a card manually</div>
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={submitting}
                    style={{
                        padding: '4px 10px',
                        backgroundColor: '#fff',
                        color: submitting ? '#a3a8a4' : '#6b716d',
                        border: '1px solid #cdd3cf',
                        borderRadius: 6,
                        fontSize: 12,
                        cursor: submitting ? 'not-allowed' : 'pointer',
                    }}
                >
                    Cancel
                </button>
            </div>

            <div style={{
                fontSize: 12,
                color: '#6b716d',
                marginBottom: 10,
                lineHeight: 1.4,
            }}>
                Card details are entered directly into NewtonPay's secure form —
                we never see the PAN or CVV. Only the resulting tokenised card lands
                on file.
            </div>

            {loading && (
                <div style={{ color: '#6b716d', fontSize: 13 }}>Loading secure card form…</div>
            )}

            {!loading && sessionKey && pmcId && (
                <>
                    {/* Element is keyed on sessionKey so a fresh session
                        always remounts a clean iframe (React would
                        otherwise reuse the existing element with stale
                        internal state). */}
                    <rainforest-payment
                        key={sessionKey}
                        ref={componentRef}
                        session-key={sessionKey}
                        payment-method-config-id={pmcId}
                        allowed-methods="CARD"
                    ></rainforest-payment>

                    <label
                        title={hasExistingCards
                            ? ''
                            : 'First card on file is always the default'}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            marginTop: 12,
                            fontSize: 13,
                            color: hasExistingCards ? '#6b716d' : '#8a908c',
                            cursor: hasExistingCards ? 'pointer' : 'not-allowed',
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={makeDefault}
                            disabled={!hasExistingCards}
                            onChange={(e) => setMakeDefault(e.target.checked)}
                            style={{ cursor: hasExistingCards ? 'pointer' : 'not-allowed' }}
                        />
                        Make this the patient's default payment method
                        {!hasExistingCards && (
                            <span style={{
                                fontSize: 11,
                                color: '#8a908c',
                                fontStyle: 'italic',
                            }}>
                                (first card is always default)
                            </span>
                        )}
                    </label>
                </>
            )}

            {submitting && (
                <div style={{ color: '#6b716d', fontSize: 13, marginTop: 8 }}>
                    Saving card…
                </div>
            )}

            {error && (
                <div style={{ color: '#b42318', fontSize: 13, marginTop: 8 }}>{error}</div>
            )}
        </div>
    );
}
